import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Typography,
  styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import * as XLSX from 'xlsx';
import { fetchWorkforceBusinessUnits } from '../WorkforceOperations/workforceBusinessUnitUtils';
import EmployeeImportRefTemplateSection from '../WorkforceOperations/EmployeeImportRefTemplateSection';
import RefTemplateMappingsEditorDrawer from '../GLSync/RefTemplateMappingsEditorDrawer';
import {
  buildColumnIndexFromTemplate,
  buildTemplateColumnMatchRows,
  fetchEmployeeImportTemplates,
  initializeExcelColumnMap,
  normalizeMappingFromApi,
} from '../WorkforceOperations/employeeImportRefTemplateUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateUploadOutlinedSx,
  harmonateSelectMenuProps,
  harmonateFormFieldsSx,
} from '../../../theme/harmonateStyles';

const MANDATORY_HEADERS = [
  { display: 'Employee Code', key: 'employeeCode', aliases: [] },
];

const OPTIONAL_HEADERS = [
  { display: 'Location Name', key: 'locationName', aliases: ['Location', 'Business Unit'] },
  { display: 'First Name', key: 'firstName', aliases: [] },
  { display: 'Last Name', key: 'lastName', aliases: [] },
  { display: 'Phone', key: 'phone', aliases: ['Mobile Phone'] },
  { display: 'Home Phone', key: 'homePhone', aliases: [] },
  { display: 'Hire Date', key: 'hireDate', aliases: [] },
  { display: 'Employment Type', key: 'employmentType', aliases: ['Emp Stat'] },
  { display: 'Status', key: 'status', aliases: [] },
  { display: 'Department', key: 'department', aliases: ['Dept'] },
  { display: 'Hourly Rate', key: 'hourlyRate', aliases: ['Pay Rate'] },
  { display: 'Job Title', key: 'jobTitle', aliases: ['Default Position'] },
  { display: 'Jobcode Level 1', key: 'jobcodeLevel1', aliases: [] },
  { display: 'Jobcode Level 2', key: 'jobcodeLevel2', aliases: [] },
];

const headerMatchNames = (header) => [header.display, ...(header.aliases || [])];

const UploadArea = styled(Box)(() => ({
  border: `2px dashed ${ht.line}`,
  borderRadius: ht.cardRadius,
  padding: '24px 16px',
  textAlign: 'center',
  backgroundColor: ht.listBg,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: ht.blue,
    backgroundColor: ht.secondaryTab,
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const normalizeHeader = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const normalizeEmploymentType = (value) => {
  const s = String(value ?? '').trim();
  if (!s) return null;
  return s.replace(/\s+/g, '_').toUpperCase();
};

const normalizeHourlyRate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const cleaned = String(value).trim().replace(/[$,\s]/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const resolveHeaderIndex = (keyMap, header) => {
  for (const name of headerMatchNames(header)) {
    const idx = keyMap[normalizeHeader(name)];
    if (idx !== undefined) return idx;
  }
  return undefined;
};

const daysInMonth = (year, month1to12) => new Date(year, month1to12, 0).getDate();

const tryCalendarDate = (year, month, day) => {
  if (!Number.isFinite(year) || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} 00:00:00`;
};

/**
 * Parses month/day/year dates: 4/1/2023, 05-23-17, 4.1.2023.
 */
const parseSlashOrDashDateString = (s) => {
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (!m) return null;
  const month = Number(m[1]);
  const day = Number(m[2]);
  const yRaw = m[3];
  const year = yRaw.length === 2 ? 2000 + Number(yRaw) : Number(yRaw);
  if (!Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(year)) return null;
  return tryCalendarDate(year, month, day);
};

const normalizeDateForApi = (value) => {
  if (value === null || value === undefined || value === '') return null;

  // Excel serial date (preferred path when cellDates is false).
  if (typeof value === 'number' && !Number.isNaN(value) && XLSX.SSF?.parse_date_code) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed && parsed.y && parsed.m && parsed.d) {
      return tryCalendarDate(parsed.y, parsed.m, parsed.d);
    }
  }

  // SheetJS Date objects are UTC midnight for the Excel calendar day — use UTC parts
  // to avoid local-timezone off-by-one (e.g. 4/1/2023 → 2023-03-31).
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return tryCalendarDate(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate());
  }

  const s = String(value).trim();
  if (!s) return null;

  const slashParsed = parseSlashOrDashDateString(s);
  if (slashParsed) return slashParsed;

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s} 00:00:00`;
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?/.test(s)) {
    return s.includes('T') ? s.replace('T', ' ').slice(0, 19) : s.slice(0, 19);
  }
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;

  return null;
};

const buildHeaderKeyMap = (headerRow) => {
  const keyMap = {};
  headerRow.forEach((cell, index) => {
    const norm = normalizeHeader(cell);
    if (norm) keyMap[norm] = index;
  });
  return keyMap;
};

const validateHeaders = (headerRow) => {
  const keyMap = buildHeaderKeyMap(headerRow);
  const columnIndexByKey = {};
  const missing = [];

  MANDATORY_HEADERS.forEach((header) => {
    const idx = resolveHeaderIndex(keyMap, header);
    if (idx === undefined) {
      missing.push(header.display);
    } else {
      columnIndexByKey[header.key] = idx;
    }
  });

  if (missing.length) {
    return { missing, columnIndexByKey: null };
  }

  OPTIONAL_HEADERS.forEach((opt) => {
    const idx = resolveHeaderIndex(keyMap, opt);
    if (idx !== undefined) {
      columnIndexByKey[opt.key] = idx;
    }
  });
  return { missing: [], columnIndexByKey };
};

const isEmptyCellValue = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (typeof value === 'number') return Number.isNaN(value);
  if (value instanceof Date) return Number.isNaN(value.getTime());
  return String(value).trim() === '';
};

const formatRawCellValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getUTCMonth() + 1}/${value.getUTCDate()}/${value.getUTCFullYear()}`;
  }
  if (typeof value === 'number' && !Number.isNaN(value) && XLSX.SSF?.parse_date_code) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed?.y && parsed?.m && parsed?.d) {
      return `${parsed.m}/${parsed.d}/${parsed.y}`;
    }
  }
  return String(value ?? '').trim();
};

const resolveDateField = (rawValue, fieldLabel) => {
  if (isEmptyCellValue(rawValue)) {
    return { ok: true, value: null };
  }
  const normalized = normalizeDateForApi(rawValue);
  if (!normalized) {
    return {
      ok: false,
      error: `${fieldLabel} "${formatRawCellValue(rawValue)}" is invalid. Use month/day/year (e.g. 4/1/2023).`,
    };
  }
  return { ok: true, value: normalized };
};

const rowArrayToPayloadItem = (rowCells, columnIndexByKey, tenantId, username, fallbackPropertyId) => {
  const get = (key) => {
    const idx = columnIndexByKey[key];
    if (idx === undefined) return '';
    const v = rowCells[idx];
    if (v === null || v === undefined) return '';
    return v;
  };

  const employeeCode = String(get('employeeCode')).trim();
  if (!employeeCode) return { skip: true };

  const locationName = String(get('locationName')).trim();
  if (!locationName && !fallbackPropertyId) {
    return {
      error: 'Location Name is missing and no Business Unit is selected.',
      employeeCode,
    };
  }

  const hireDateResult = resolveDateField(get('hireDate'), 'Hire Date');
  if (!hireDateResult.ok) {
    return { error: hireDateResult.error, employeeCode };
  }

  const employmentType = normalizeEmploymentType(get('employmentType'));
  const hourlyRate = normalizeHourlyRate(get('hourlyRate'));
  const jobTitle = String(get('jobTitle')).trim() || null;

  return {
    item: {
      tenantId,
      ...(locationName ? { locationName } : {}),
      ...(fallbackPropertyId && !locationName ? { primaryPropertyId: fallbackPropertyId } : {}),
      ...(jobTitle ? { jobTitle } : {}),
      employeeCode,
      firstName: String(get('firstName')).trim() || null,
      lastName: String(get('lastName')).trim() || null,
      phone: String(get('phone')).trim() || null,
      homePhone: String(get('homePhone')).trim() || null,
      hireDate: hireDateResult.value,
      employmentType,
      status: String(get('status')).trim() || null,
      department: String(get('department')).trim() || null,
      hourlyRate,
      jobcodeLevel1: String(get('jobcodeLevel1')).trim() || null,
      jobcodeLevel2: String(get('jobcodeLevel2')).trim() || null,
      createdBy: username,
      updatedBy: username,
    },
  };
};

const collectRowDateErrors = (bodyRows, colMap) => {
  const errors = [];
  const maxIdx = Math.max(0, ...Object.values(colMap));
  bodyRows.forEach((row, index) => {
    const extendedRow = [];
    for (let i = 0; i <= maxIdx; i += 1) {
      extendedRow[i] = row[i] !== undefined && row[i] !== null ? row[i] : '';
    }
    const codeIdx = colMap.employeeCode;
    const employeeCode =
      codeIdx !== undefined ? String(extendedRow[codeIdx] ?? '').trim() : '';
    if (!employeeCode) return;

    const hireIdx = colMap.hireDate;
    if (hireIdx === undefined) return;
    const rawHire = extendedRow[hireIdx];
    const hireDateResult = resolveDateField(rawHire, 'Hire Date');
    if (!hireDateResult.ok) {
      errors.push(`Row ${index + 2} (Employee Code ${employeeCode}): ${hireDateResult.error}`);
    }
  });
  return errors;
};

const parseWorkbookToGrid = (workbook) => {
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const worksheet = workbook.Sheets[sheetName];
  return XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: true });
};

const formatMandatoryHeaderLabel = (header) =>
  header.aliases?.length ? `${header.display} (${header.aliases.join(', ')})` : header.display;

const GlobalEmployeeImportDialog = ({
  open,
  onClose,
  tenantId,
  username,
  onImportSuccess,
  showSuccess,
  showError,
}) => {
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [headerError, setHeaderError] = useState('');
  const [rowCount, setRowCount] = useState(0);
  const [columnIndexByKey, setColumnIndexByKey] = useState(null);
  const [dataRows, setDataRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [headerRow, setHeaderRow] = useState([]);
  const [sheetFields, setSheetFields] = useState([]);
  const [rawBodyRows, setRawBodyRows] = useState([]);
  const [refTemplates, setRefTemplates] = useState([]);
  const [refTemplatesLoading, setRefTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [localMappingById, setLocalMappingById] = useState({});
  const [excelColumnByTemplateField, setExcelColumnByTemplateField] = useState({});
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const fileInputRef = useRef(null);

  const getAuthToken = useCallback(() => localStorage.getItem('token') || '', []);

  const activeTemplate = useMemo(() => {
    const base = refTemplates.find((t) => t.id === selectedTemplateId);
    if (!base) return null;
    const override = localMappingById[base.id];
    if (!override) return base;
    return { ...base, mappings: normalizeMappingFromApi(override) };
  }, [refTemplates, selectedTemplateId, localMappingById]);

  const templateMatchRows = useMemo(
    () => buildTemplateColumnMatchRows(activeTemplate, excelColumnByTemplateField),
    [activeTemplate, excelColumnByTemplateField]
  );

  useEffect(() => {
    if (!open || !tenantId) return;
    let cancelled = false;
    fetchWorkforceBusinessUnits(tenantId)
      .then((units) => {
        if (cancelled) return;
        setBusinessUnits(Array.isArray(units) ? units : []);
      })
      .catch(() => {
        if (!cancelled) setBusinessUnits([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tenantId]);

  useEffect(() => {
    if (!open || !tenantId) return;
    let cancelled = false;
    setRefTemplatesLoading(true);
    const token = localStorage.getItem('token') || '';
    fetchEmployeeImportTemplates(process.env.REACT_APP_BACKEND_URL || '', tenantId, {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    })
      .then(({ templates }) => {
        if (!cancelled) setRefTemplates(templates);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setRefTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setRefTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tenantId]);

  useEffect(() => {
    if (!activeTemplate || !sheetFields.length) {
      setExcelColumnByTemplateField({});
      return;
    }
    setExcelColumnByTemplateField(initializeExcelColumnMap(activeTemplate, sheetFields));
  }, [activeTemplate, sheetFields]);

  const applyColumnsFromState = useCallback(
    (nextHeaderRow, bodyRows, template, columnMapFromTemplate) => {
      if (template) {
        const colMap =
          columnMapFromTemplate ||
          buildColumnIndexFromTemplate(
            nextHeaderRow,
            initializeExcelColumnMap(template, nextHeaderRow.map((c) => String(c ?? '').trim()).filter(Boolean))
          );
        if (colMap.employeeCode === undefined) {
          setHeaderError(
            'Spreadsheet template must map Employee Code to a column present in the file.'
          );
          setColumnIndexByKey(null);
          setDataRows([]);
          setRowCount(0);
          return;
        }
        const dateErrors = collectRowDateErrors(bodyRows, colMap);
        if (dateErrors.length) {
          setHeaderError('');
          setParseError(
            `Import blocked — fix invalid date(s) and try again. Dates must be month/day/year (M/D/YYYY).\n${dateErrors.join('\n')}`
          );
          setColumnIndexByKey(null);
          setDataRows([]);
          setRowCount(0);
          return;
        }
        setHeaderError('');
        setParseError('');
        setColumnIndexByKey(colMap);
        setDataRows(bodyRows);
        setRowCount(bodyRows.length);
        if (bodyRows.length === 0) {
          setParseError('No data rows found after the header row.');
        }
        return;
      }

      const { missing, columnIndexByKey: colMap } = validateHeaders(nextHeaderRow);
      if (missing.length) {
        const optionalNames = OPTIONAL_HEADERS.map((h) =>
          h.aliases?.length ? `${h.display} (or ${h.aliases.join(', ')})` : h.display
        ).join(', ');
        const requiredNames = MANDATORY_HEADERS.map(formatMandatoryHeaderLabel).join(', ');
        setHeaderError(
          `Missing required column(s): ${missing.join(', ')}. The first row must include: ${requiredNames}. Optional columns: ${optionalNames}.`
        );
        setColumnIndexByKey(null);
        setDataRows([]);
        setRowCount(0);
        return;
      }
      const dateErrors = collectRowDateErrors(bodyRows, colMap);
      if (dateErrors.length) {
        setHeaderError('');
        setParseError(
          `Import blocked — fix invalid date(s) and try again. Dates must be month/day/year (M/D/YYYY).\n${dateErrors.join('\n')}`
        );
        setColumnIndexByKey(null);
        setDataRows([]);
        setRowCount(0);
        return;
      }
      setHeaderError('');
      setParseError('');
      setColumnIndexByKey(colMap);
      setDataRows(bodyRows);
      setRowCount(bodyRows.length);
      if (bodyRows.length === 0) {
        setParseError('No data rows found after the header row.');
      }
    },
    []
  );

  useEffect(() => {
    if (!headerRow.length || !rawBodyRows.length) return;
    if (activeTemplate) {
      const colMap = buildColumnIndexFromTemplate(headerRow, excelColumnByTemplateField);
      applyColumnsFromState(headerRow, rawBodyRows, activeTemplate, colMap);
    } else {
      applyColumnsFromState(headerRow, rawBodyRows, null, null);
    }
  }, [activeTemplate, excelColumnByTemplateField, headerRow, rawBodyRows, applyColumnsFromState]);

  const resetState = useCallback(() => {
    setFileName('');
    setParseError('');
    setHeaderError('');
    setRowCount(0);
    setColumnIndexByKey(null);
    setDataRows([]);
    setRawBodyRows([]);
    setLastResult(null);
    setHeaderRow([]);
    setSheetFields([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = () => {
    if (!submitting) {
      resetState();
      setSelectedPropertyId('');
      setSelectedTemplateId('');
      setLocalMappingById({});
      setExcelColumnByTemplateField({});
      onClose();
    }
  };

  const processFile = useCallback(
    (file) => {
      if (!file) return;

      resetState();
      setFileName(file.name);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: false });
          const grid = parseWorkbookToGrid(workbook);
          if (!grid.length) {
            setParseError('The file is empty.');
            return;
          }
          const nextHeaderRow = grid[0].map((c) => (c === null || c === undefined ? '' : c));
          const fields = nextHeaderRow.map((c) => String(c ?? '').trim()).filter(Boolean);
          const bodyRows = grid.slice(1).filter((row) => row.some((cell) => String(cell ?? '').trim() !== ''));
          setHeaderRow(nextHeaderRow);
          setSheetFields(fields);
          setRawBodyRows(bodyRows);

          const template = activeTemplate;
          const colMapFromTemplate = template
            ? buildColumnIndexFromTemplate(nextHeaderRow, initializeExcelColumnMap(template, fields))
            : null;
          if (template) {
            setExcelColumnByTemplateField(initializeExcelColumnMap(template, fields));
          }
          applyColumnsFromState(nextHeaderRow, bodyRows, template, colMapFromTemplate);
        } catch (err) {
          console.error(err);
          setParseError(err.message || 'Could not read the file. Use .xlsx, .xls, or .csv.');
        }
      };
      reader.onerror = () => {
        setParseError('Failed to read the file.');
      };
      reader.readAsArrayBuffer(file);
    },
    [resetState, activeTemplate, applyColumnsFromState]
  );

  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    processFile(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!submitting) setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (submitting) return;
    const file = e.dataTransfer.files?.[0];
    processFile(file);
  };

  const hasLocationColumn = Boolean(columnIndexByKey?.locationName);

  const handleSubmit = async () => {
    if (!tenantId) {
      (showError || (() => {}))('Tenant is required');
      return;
    }
    if (!columnIndexByKey || !dataRows.length) {
      (showError || (() => {}))('Choose a valid file with data rows first');
      return;
    }
    if (!hasLocationColumn && !selectedPropertyId) {
      (showError || (() => {}))(
        'Select a Business Unit, or include a Location Name column in the file.'
      );
      return;
    }

    const payload = [];
    const rowErrors = [];
    const maxIdx = Math.max(0, ...Object.values(columnIndexByKey));
    dataRows.forEach((row, index) => {
      const extendedRow = [];
      for (let i = 0; i <= maxIdx; i += 1) {
        extendedRow[i] = row[i] !== undefined && row[i] !== null ? row[i] : '';
      }
      const parsed = rowArrayToPayloadItem(
        extendedRow,
        columnIndexByKey,
        tenantId,
        username,
        selectedPropertyId || null
      );
      if (parsed?.skip) return;
      if (parsed?.error) {
        rowErrors.push(`Row ${index + 2} (Employee Code ${parsed.employeeCode}): ${parsed.error}`);
        return;
      }
      if (parsed?.item) {
        payload.push(parsed.item);
      }
    });

    if (rowErrors.length) {
      const message = `Import blocked — fix invalid value(s) and try again.\n${rowErrors.join('\n')}`;
      setParseError(message);
      (showError || (() => {}))(message);
      return;
    }

    if (payload.length === 0) {
      (showError || (() => {}))(
        'No valid rows (Employee Code is required; Location Name or a selected Business Unit is required).'
      );
      return;
    }

    const token = localStorage.getItem('token') || '';
    try {
      setSubmitting(true);
      setLastResult(null);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/employees/bulk-global-import`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || errBody.message || `Import failed: ${response.status}`);
      }

      const result = await response.json();
      const succeeded = Array.isArray(result.succeeded) ? result.succeeded : [];

      setLastResult(result);
      const failCount = result.failureCount ?? (Array.isArray(result.failed) ? result.failed.length : 0);
      const okCount = result.successCount ?? succeeded.length;

      if (okCount > 0 && onImportSuccess) {
        onImportSuccess();
      }

      if (failCount === 0) {
        (showSuccess || (() => {}))(`Imported ${okCount} employee(s) successfully`);
        resetState();
        setSelectedPropertyId('');
        onClose();
      } else {
        (showError || (() => {}))(
          `Imported with ${okCount} succeeded and ${failCount} failed — see details below.`
        );
      }
    } catch (err) {
      console.error(err);
      (showError || (() => {}))(err.message || 'Bulk import failed');
    } finally {
      setSubmitting(false);
    }
  };

  const canImport = Boolean(
    columnIndexByKey &&
      dataRows.length > 0 &&
      !headerError &&
      !parseError &&
      (hasLocationColumn || selectedPropertyId)
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        ...harmonateDialogPaperProps,
        sx: { ...harmonateDialogPaperProps.sx, maxHeight: '95vh' },
      }}
    >
      <DialogTitle sx={harmonateDialogTitleSx}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, color: ht.white }}>
          Import employees
        </Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small" aria-label="close" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx, display: 'flex', flexDirection: 'column', gap: 0 }}>
        <Box
          sx={{
            mb: 3,
            p: 2,
            mx: 0,
            bgcolor: ht.listBg,
            border: `1px solid ${ht.line}`,
            borderRadius: ht.fieldRadius,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assign a Business Unit below to apply to all rows. If a row has Location Name, that value
            wins; otherwise the selected Business Unit is used.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="global-employee-import-bu-label" shrink>
              Business Unit
            </InputLabel>
            <Select
              labelId="global-employee-import-bu-label"
              label="Business Unit"
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              disabled={submitting}
              displayEmpty
              notched
              MenuProps={harmonateSelectMenuProps}
            >
              <MenuItem value="">
                <em>None — use Location Name from file</em>
              </MenuItem>
              {businessUnits.map((unit) => {
                const id = unit.propertyId || unit.businessUnitId || unit.id;
                if (!id) return null;
                const name =
                  unit.businessUnitName || unit.propertyName || unit.PROPERTY_NAME || String(id);
                return (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: ht.listBg,
            border: `1px solid ${ht.line}`,
            borderRadius: ht.fieldRadius,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Column headers (first row)
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Required:</strong>{' '}
            {MANDATORY_HEADERS.map(formatMandatoryHeaderLabel).join(', ')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Optional:</strong>{' '}
            {OPTIONAL_HEADERS.map((h) =>
              h.aliases?.length ? `${h.display} (${h.aliases.join(', ')})` : h.display
            ).join(', ')}
          </Typography>
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Upload file
        </Typography>
        <DialogContentText sx={{ mb: 2, color: 'text.secondary' }}>
          Drag and drop an Excel or CSV file here, or click to browse. Each data row must include{' '}
          <strong>Employee Code</strong>, plus either <strong>Location Name</strong> or a selected
          Business Unit.
        </DialogContentText>

        <HiddenInput
          ref={fileInputRef}
          id="global-employee-import-file-input"
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileInputChange}
        />
        <label
          htmlFor="global-employee-import-file-input"
          style={{
            display: 'block',
            cursor: submitting ? 'default' : 'pointer',
            marginBottom: 16,
          }}
        >
          <UploadArea
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              borderColor: isDragging ? ht.green : undefined,
              backgroundColor: isDragging ? ht.secondaryTab : undefined,
              pointerEvents: submitting ? 'none' : 'auto',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? ht.green : ht.blue, mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              Drop file here or click to browse
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supports .xlsx, .xls, .csv
            </Typography>
          </UploadArea>
        </label>

        {fileName ? (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: ht.listBg,
              border: `1px solid ${ht.line}`,
              borderRadius: ht.fieldRadius,
            }}
          >
            <InsertDriveFileIcon color="action" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap title={fileName}>
                {fileName}
              </Typography>
              {rowCount > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {rowCount} data row(s)
                  {hasLocationColumn ? ' · Location Name column detected' : ''}
                </Typography>
              ) : null}
            </Box>
            <Button size="small" variant="outlined" onClick={resetState} disabled={submitting} sx={harmonateUploadOutlinedSx}>
              Clear
            </Button>
          </Paper>
        ) : null}

        <EmployeeImportRefTemplateSection
          templates={refTemplates}
          selectedTemplateId={selectedTemplateId}
          onSelectTemplateId={setSelectedTemplateId}
          activeTemplate={activeTemplate}
          matchRows={templateMatchRows}
          sheetFields={sheetFields}
          onEditMapping={() => setTemplateEditorOpen(true)}
          disabled={submitting}
          loading={refTemplatesLoading}
        />

        {parseError ? (
          <Typography color="error" variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
            {parseError}
          </Typography>
        ) : null}
        {headerError ? (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {headerError}
          </Typography>
        ) : null}

        {lastResult && (lastResult.failed?.length > 0 || lastResult.succeeded?.length >= 0) ? (
          <Box sx={{ border: `1px solid ${ht.line}`, borderRadius: ht.fieldRadius, p: 1.5, mt: 1, bgcolor: ht.listBg }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Result: {lastResult.successCount ?? 0} succeeded, {lastResult.failureCount ?? 0} failed
            </Typography>
            {lastResult.failed?.length > 0 ? (
              <List dense disablePadding>
                {lastResult.failed.map((f, i) => (
                  <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={`Row ${typeof f.index === 'number' ? f.index + 2 : '—'} — ${f.employeeCode || 'unknown code'}`}
                      secondary={f.error}
                    />
                  </ListItem>
                ))}
              </List>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canImport || submitting}
          sx={harmonateProceedButtonSx}
        >
          {submitting ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>

      <RefTemplateMappingsEditorDrawer
        open={templateEditorOpen}
        onClose={() => setTemplateEditorOpen(false)}
        apiTemplate={
          activeTemplate?.apiTemplate
            ? {
                ...activeTemplate.apiTemplate,
                mapping: activeTemplate.mappings,
              }
            : null
        }
        getAuthToken={getAuthToken}
        persistToApi={false}
        onLocalMappingChange={(templateId, mapping) => {
          setLocalMappingById((prev) => ({ ...prev, [String(templateId)]: mapping }));
        }}
      />
    </Dialog>
  );
};

export default GlobalEmployeeImportDialog;
